// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { navigationDrawerStyles } from "@awc/Overrides/Components/NavigationDrawer";
import ChevronRight from "@awc/XDL/Icons/ChevronRight";
import Close from "@awc/XDL/Icons/Close";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { color } from "@awc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

navigationDrawerStyles.menuTriggerButton = {
    variant: "link" as any,
    css: css`
        width: 44px;
        height: 44px;
        display: flex;
        justify-content: center;
        align-items: center;
        &&& ${IconWrapper} {
            color: ${color("text.main")};
        }
    `,
};

navigationDrawerStyles.drawer = {
    size: 278,
    cssOverrides: {
        drawerBody: css`
            top: 0;
            transform: translate(-100%, 0);
            height: 100vh;
            box-shadow: none;
            padding: 0 0 16px 0;
            background-color: ${color("primary.main")};
            overflow-y: hidden;
        `,
        drawerTitle: css`
            background-color: ${color("primary.main")};
            border-bottom: 1px solid ${color("common.white", 0.2)};
            padding: 0;
        `,
        scrim: css`
            background-color: ${color("common.black", 0.3)};
            top: 0;
            height: 100vh;
        `,
    },
    closeButtonProps: {
        css: css`
            &,
            &:focus,
            &:hover {
                background: transparent;
                border: 0;
                padding: 16px 12px;
            }
        `,
    },
    closeButtonIconProps: { size: 15, src: Close },
};

navigationDrawerStyles.drawerSectionWrapper = {
    css: css``,
};

navigationDrawerStyles.mainNavigationRow = {
    color: "common.ui1",
    css: css`
        margin: 0;
        padding: 16px 12px 8px 18px;
        background-color: ${color("primary.main")};
        &,
        ${IconWrapper}, ${TypographyStyle} {
            color: ${color("common.white")};
        }

        &:hover,
        &:focus {
            background-color: ${color("common.ui8")};
            ${IconWrapper}, ${TypographyStyle} {
                color: ${color("common.white")};
            }
        }
    `,
    moreIconProps: {
        src: ChevronRight,
        size: 14,
        css: css`
            color: ${color("common.ui1")};
            height: 20px;
            width: 20px;
            svg {
                width: auto;
            }
            justify-content: flex-end;
        `,
    },
};

navigationDrawerStyles.mainNavigationRowTypography = {
    color: "common.ui1",
    weight: fontConfig.weight.medium,
    size: fontConfig.fontSize.regular,
    lineHeight: fontConfig.lineHeight.singleLineRegular,
    css: css`
        max-width: 217px;
    `,
};

navigationDrawerStyles.drawerSectionSeparator = {
    css: css`
        margin: 16px auto;
        background-color: ${color("common.border")};
        height: 1px;
        width: 100%;
    `,
};

navigationDrawerStyles.accountPanelSectionWrapper = {
    css: css`
        display: flex;
        align-items: center;
        width: 100%;
    `,
};

navigationDrawerStyles.panelMenu = {
    bodyColor: "common.ui1",
    headerColor: "primary.main",
    panelChildrenSeparator: {
        css: css`
            margin: 0 auto 8px auto;
            background-color: ${color("common.border")};
            height: 1px;
            width: calc(100% - 32px);
        `,
    },
    panelRowProps: {
        css: css`
            background-color: ${color("common.ui9")};
        `,
    },
    cssOverrides: {
        menu: css`
            padding: 0 0 16px 0;
            background-color: ${color("common.ui9")};
            &:has(.panel-menu) {
                &::after {
                    content: "";
                    width: 1px;
                    height: 100%;
                    position: absolute;
                    right: 30px;
                    top: 50px;
                    background-color: ${color("common.white")};
                    opacity: 0.3;
                }
            }
        `,
        children: css`
            display: flex;
            flex-direction: column;
        `,
        wrapper: css`
            ${StyledButton} {
                color: ${color("common.ui1")};
                padding: 16px 12px 8px 18px;
                text-align: left;
                ${TypographyStyle} {
                    max-width: 217px;
                }
                &:hover,
                &:focus {
                    background-color: ${color("common.ui8")};
                    ${IconWrapper}, ${TypographyStyle} {
                        color: ${color("common.ui1")};
                    }
                }
            }
        `,
    },
};
navigationDrawerStyles.logoLinks = {
    color: "primary.main",
    typographyProps: {
        ellipsis: true,
        transform: "none",
        color: "common.ui1",
        lineHeight: fontConfig.lineHeight.singleLineRegular,
        weight: fontConfig.weight.medium,
    },
    css: css`
        margin: 0;
        padding: 16px 12px 8px 18px;
        &:has(> span:empty) {
            display: none;
        }
    `,
};

navigationDrawerStyles.signOutButton = {
    color: "common.ui1",
    typographyProps: {
        weight: fontConfig.weight.medium,
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.singleLineRegular,
    },
};

navigationDrawerStyles.currencySymbol = {
    color: "common.white",
    size: 14,
    css: css`
        margin-right: 8px;
        display: inline-block;
        vertical-align: middle;
        width: 14px;
    `,
};

navigationDrawerStyles.menuRowIcon = {
    color: "common.white",
    size: 14,
    css: css`
        margin-right: 8px;
        display: inline-block;
        vertical-align: middle;
    `,
};
